<template>
    <div class="Pagination">
        <div
            class="Pagination__previous"
            :class="{'Pagination__previous--disabled': currentPage === 1}"
            @click="previous"
        >
            <FontAwesomeIcon :icon="faAngleLeft" />
        </div>
        <span
            v-for="(page, index) in buildPages()"
            :key="index"
            class="Pagination__pageIndex"
            :class="{'Pagination__ellipse': page === '...', 'Pagination__current': page === String(currentPage)}"
            @click="navigate(page)"
        >{{ page }}</span>
        <div
            class="Pagination__next"
            :class="{'Pagination__next--disabled': currentPage === getTotalPages()}"
            @click="next"
        >
            <FontAwesomeIcon :icon="faAngleRight" />
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import range from 'lodash/range';

    import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    // PRODUCT-26110 Task 12 (COMPONENT_V_MODEL): migrated to the Vue 3 v-model
    // contract (emit rename only -- this component has never declared an inbound
    // `value`/`modelValue` prop; its only consumer, BulkImportCorrectionTable.vue,
    // v-models it purely to receive the paginated slice back, a pre-existing
    // one-way-in-practice usage that this rename preserves unchanged).
    @Component({
        emits: [ 'update:modelValue' ],
        components: { FontAwesomeIcon },
    })
    export default class Pagination extends Vue {
        private faAngleLeft = faAngleLeft;
        private faAngleRight = faAngleRight;

        @Prop({ type: Number, default: 20 })
        private perPage!: number;

        @Prop({ type: Array, default: () => [] })
        private items!: any[];

        private currentPage = 1;

        private getTotalPages(): number {
            return Math.ceil(this.items.length / this.perPage || 0);
        }

        @Watch('items', { immediate: true })
        private onCurrentItemsChanged(): void {
            this.navigate(this.currentPage);
        }

        private buildPages(): string[] {
            const totalPages = this.getTotalPages();
            const first5 = range(1, 6);
            const last5 = range(5, 0).map((i) => (totalPages - i));
            let pages;
            if (totalPages <= 5) {
                pages = range(1, totalPages + 1);
            } else if (this.currentPage < 5) {
                pages = [ ...first5, '...', totalPages ];
            } else if (this.currentPage > totalPages - 5) {
                pages = [ '1', '...', ...last5 ];
            } else {
                pages = [
                    '1',
                    '...',
                    this.currentPage - 1,
                    this.currentPage,
                    this.currentPage + 1,
                    '...',
                    totalPages,
                ];
            }
            return pages.map((page) => `${page}`);
        }

        private previous(): void {
            if (this.currentPage > 1) {
                this.navigate(`${this.currentPage - 1}`);
            }
        }

        private next(): void {
            if (this.currentPage < this.getTotalPages()) {
                this.navigate(`${this.currentPage + 1}`);
            }
        }

        public navigate(page: string | number): void {
            const destinationPage = parseInt(`${page}`, 10);
            if (isNaN(destinationPage)) {
                return;
            }
            this.currentPage = destinationPage;
            const startIndex = Math.max(0, this.currentPage - 1) * this.perPage;
            const endIndex = startIndex + this.perPage;
            this.$emit('update:modelValue', this.items.slice(startIndex, endIndex));
        }
    }
</script>
